<template>
  <div class="admin-layout admin-layout-modern">
    <!-- Header -->
    <div class="admin-header">
      <div class="container">
        <div class="header-content">
          <h1 class="admin-title">Teamio AI Dashboard</h1>
          <div class="admin-controls">
            <!-- Date-range picker — the SINGLE source of truth is the botAdmin store's
                 dashboardRange (default 'all'). Selecting an option commits the new key;
                 the Dashboard tiles + Appointment Stats page watch the getter and re-fetch.
                 Options map 1:1 to the backend-supported keys (lib/dashboardStats.js
                 SUPPORTED_RANGES); the endpoint computes the site-local window itself. -->
            <div class="date-filter">
              <label for="date-range">Date Range:</label>
              <select id="date-range" :value="dashboardRange" @change="onRangeChange" class="form-control">
                <option v-for="opt in rangeOptions" :key="opt.key" :value="opt.key">{{ opt.label }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="admin-container container">
      <div class="admin-content">
        <!-- Sidebar Navigation. On desktop/tablet this is a vertical rail beside the
             content; on mobile (<768px) it collapses into a horizontal, swipeable tab
             strip pinned across the top (see .admin-sidebar media query) so every
             destination stays one tap away without a hamburger. -->
        <div class="admin-sidebar">
          <nav class="nav-menu" aria-label="AI dashboard sections">
            <router-link :to="{ name: 'CalendarAdminDashboard' }" class="nav-item" exact-active-class="router-link-active">
              <span class="nav-icon">📊</span>
              <span class="nav-text">Dashboard</span>
            </router-link>
            <router-link v-if="$can('read', 'ai-call-history')" :to="{ name: 'CalendarAdminBotConversationList' }" class="nav-item" exact-active-class="router-link-active">
              <span class="nav-icon">💬</span>
              <span class="nav-text">Bot Conversations</span>
            </router-link>
            <router-link v-if="$can('read', 'ai-call-history')" :to="{ name: 'CalendarAdminKnowledge' }" class="nav-item" exact-active-class="router-link-active">
              <span class="nav-icon">📚</span>
              <span class="nav-text">Knowledge</span>
            </router-link>
            <router-link :to="{ name: 'CalendarAdminAppointmentStats' }" class="nav-item" exact-active-class="router-link-active">
              <span class="nav-icon">📅</span>
              <span class="nav-text">Appointment Stats</span>
            </router-link>
          </nav>
        </div>

        <!-- Main Content Area -->
        <div class="admin-main">
          <router-view />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { SUPPORTED_RANGES, rangeLabel } from '@/utils/dashboardStats';

export default {
  name: 'AdminLayout',
  computed: {
    // The selected range lives in the botAdmin store (single source of truth) so the
    // picker, the Dashboard tiles, and the Appointment Stats page never disagree.
    ...mapGetters('botAdmin', { dashboardRange: 'dashboardRange' }),
    // Picker options derived from the backend-supported keys + their human labels,
    // so the select never offers a key the endpoint can't window ('all' is first/default).
    rangeOptions() {
      return SUPPORTED_RANGES.map((key) => ({ key, label: rangeLabel(key) }));
    },
  },
  methods: {
    ...mapActions('botAdmin', ['setDashboardRange']),
    onRangeChange(event) {
      this.setDashboardRange(event.target.value);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../AppointmentCalendarModern.scss';
</style>

<style>
/* Inter font for the dashboard surface — same Google Fonts source TxPresenter uses
   (TxPresenterDashboard.vue). Unscoped @import so the @font-face is registered once;
   the `--aid-font` family below scopes it to .admin-layout so the rest of the app
   keeps its own global font. */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;450;500;550;600;700&display=swap');

/* ─── Treatment Presenter design tokens ───────────────────────────────────────
   Defined ONCE on the AdminLayout root wrapper so every child page (Dashboard,
   Appointment Stats, Knowledge, Bot Conversations) inherits them via the CSS
   cascade — custom properties cross scoped-style boundaries through the DOM.
   Mirrors src/pages/TxPresenter/* (--txb-* / --bl-*) so the two surfaces read as
   one product. */
.admin-layout {
  --aid-bg: #f7f8fa;
  --aid-surface: #ffffff;
  --aid-surface2: #f1f3f6;
  --aid-border: #e4e7ec;
  --aid-border-strong: #cdd3dc;
  --aid-text: #0f172a;
  --aid-text-mute: #475569;
  --aid-text-dim: #94a3b8;
  --aid-accent: #0f6e63;
  --aid-accent-soft: #e6f1ef;
  --aid-accent-ink: #094840;
  --aid-success: #068586;
  --aid-warn-bg: #fef3c7;
  --aid-warn-text: #b45309;
  --aid-danger-bg: #fef2f2;
  --aid-danger-text: #b91c1c;
  --aid-shadow-sm: 0 1px 2px rgba(15, 23, 42, 0.04);
  --aid-shadow-md: 0 2px 6px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04);
  --aid-shadow-card: 0 6px 18px rgba(15, 23, 42, 0.06), 0 1px 3px rgba(15, 23, 42, 0.04);
  --aid-font: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;

  /* Back-compat aliases — older child styles still reference these var names;
     point them at the new token set so nothing falls back to the old green. */
  --primary-color: var(--aid-accent);
  --primary-dark: var(--aid-accent-ink);
  --primary-light: var(--aid-accent-soft);
  --text-color: var(--aid-text-mute);
  --text-dark: var(--aid-text);
  --text-light: var(--aid-text-dim);
  --background-color: var(--aid-bg);
  --card-background: var(--aid-surface);
  --border-color: var(--aid-border);
  --border-radius: 10px;
  --shadow: var(--aid-shadow-card);
  --transition: all 0.15s ease;

  font-family: var(--aid-font);
  color: var(--aid-text);
  font-size: 14px;
  line-height: 1.45;
  background-color: var(--aid-bg);
  min-height: 100vh;
  -webkit-font-smoothing: antialiased;
}

.admin-header {
  background-color: var(--aid-surface);
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--aid-border);
  box-shadow: none;
  position: sticky;
  top: 0;
  z-index: 20;
}

.container {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
}

.admin-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: var(--aid-text);
  letter-spacing: -0.1px;
}

.admin-controls {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.date-filter {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--aid-text-mute);
}

.date-filter label {
  margin: 0;
}

/* Date-range select — TxPresenter select look (.bl-select): white bg, soft border,
   6px radius, our own chevron, teal focus ring. Native OS chrome stripped. */
.date-filter .form-control {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding: 7px 30px 7px 10px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  border: 1px solid var(--aid-border);
  border-radius: 6px;
  color: var(--aid-text);
  background-color: var(--aid-surface);
  background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2010%206%22%3E%3Cpath%20fill%3D%22%23475569%22%20d%3D%22M0%200l5%206%205-6z%22%2F%3E%3C%2Fsvg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 10px 6px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.date-filter .form-control:hover {
  border-color: var(--aid-border-strong);
}

.date-filter .form-control:focus {
  outline: none;
  border-color: var(--aid-accent);
  box-shadow: 0 0 0 2px rgba(15, 110, 99, 0.12);
}

.date-filter .form-control::-ms-expand {
  display: none;
}

.admin-container {
  padding-top: 1.5rem;
  padding-bottom: 2rem;
}

.admin-content {
  display: flex;
  gap: 1.5rem;
}

.admin-sidebar {
  flex: 0 0 220px;
  background-color: var(--aid-surface);
  border-radius: 10px;
  box-shadow: var(--aid-shadow-card);
  border: 1px solid var(--aid-border);
  padding: 0.6rem;
  height: fit-content;
}

.nav-menu {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* Sidebar nav item — TxPresenter active/selected treatment: inactive items use the
   muted ink, hover lightens to surface2; the active item gets the teal text on the
   soft-teal background plus a teal left rail (mirrors .txb-plan-pill.active /
   .txb-view-tab.active). 44px tap target preserved. */
.nav-item {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.6rem 0.85rem;
  border-radius: 8px;
  border-left: 3px solid transparent;
  color: var(--aid-text-mute);
  text-decoration: none;
  transition: var(--transition);
  font-size: 13px;
  font-weight: 500;
  min-height: 44px; /* comfortable tap target on touch devices */
}

.nav-item.router-link-active {
  background-color: var(--aid-accent-soft);
  border-left-color: var(--aid-accent);
  color: var(--aid-accent-ink);
  font-weight: 600;
}

.nav-item.router-link-active .nav-icon {
  color: var(--aid-accent);
}

.nav-item:hover:not(.router-link-active) {
  background-color: var(--aid-surface2);
  color: var(--aid-text);
}

.nav-icon {
  font-size: 1.05rem;
  width: 1.4rem;
  text-align: center;
}

.admin-main {
  flex: 1;
  background-color: transparent; /* Remove card background - content has its own cards */
  border-radius: 0;
  box-shadow: none;
  border: none; /* Remove border so inner panels don't look nested */
  padding: 0; /* Content components manage their own padding */
  min-height: 500px;
}

/* ===== Responsive Styles ===== */

/* Tablet (768–991px): keep the side-by-side rail but make it narrower so the
   content area keeps room for the 2-up tiles / tables. */
@media (max-width: 991px) and (min-width: 768px) {
  .admin-content {
    gap: 1rem;
  }

  .admin-sidebar {
    flex: 0 0 180px;
  }

  .nav-item {
    padding: 0.65rem 0.75rem;
    font-size: 0.875rem;
    gap: 0.5rem;
  }
}

/* Mobile (<768px): the rail collapses into a horizontal, swipeable tab strip
   pinned across the top of the content. Every section stays one tap away — no
   hamburger, no hidden menu. The strip scrolls sideways if the four tabs don't
   fit, and each tab is an icon-over-label pill so the labels never truncate. */
@media (max-width: 767px) {
  .container {
    padding: 0 1rem;
  }

  .admin-content {
    flex-direction: column;
    gap: 1rem;
  }

  .admin-sidebar {
    flex: 0 0 auto;
    width: 100%;
    padding: 0.4rem;
    position: sticky;
    top: 0;
    z-index: 5;
  }

  .nav-menu {
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 0.35rem;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin; /* Firefox: slim scrollbar */
  }

  /* Slim, unobtrusive scrollbar for the tab strip on WebKit. */
  .nav-menu::-webkit-scrollbar {
    height: 4px;
  }
  .nav-menu::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 4px;
  }

  /* As a horizontal tab strip the left rail reads oddly, so the active tab gets a
     2px teal underline instead (still the same teal-on-soft active treatment). */
  .nav-item {
    flex: 0 0 auto;
    flex-direction: column;
    gap: 0.2rem;
    padding: 0.5rem 0.85rem;
    font-size: 0.75rem;
    font-weight: 600;
    text-align: center;
    min-height: 56px;
    white-space: nowrap;
    justify-content: center;
    border-left: none;
    border-bottom: 2px solid transparent;
  }

  .nav-item.router-link-active {
    border-left-color: transparent;
    border-bottom-color: var(--aid-accent);
  }

  .nav-icon {
    width: auto;
    font-size: 1.15rem;
  }
}

/* Small phones (≤390px): a touch tighter so all four tabs are reachable. */
@media (max-width: 400px) {
  .admin-title {
    font-size: 1.125rem;
  }

  .header-content {
    gap: 0.5rem;
  }

  .admin-controls,
  .date-filter {
    width: 100%;
  }

  .date-filter .form-control {
    flex: 1;
  }

  .nav-item {
    padding: 0.5rem 0.7rem;
  }
}
</style> 